import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useReducedMotion } from '@/hooks/use-reduced-motion'

const STRIP = Array.from({ length: 20 }, (_, index) => index % 10)

interface DigitStripProps {
    digit: number
    active: boolean
    instant: boolean
    delay: number
    duration: number
}

interface StripState {
    position: number
    animate: boolean
}

/**
 * One odometer wheel. Holds 0-9 twice so every change rolls forward:
 * it snaps to the current digit in the first cycle, then rolls to the
 * new digit in the second cycle.
 */
const DigitStrip: FC<DigitStripProps> = ({ digit, active, instant, delay, duration }): ReactNode => {
    const [state, setState] = useState<StripState>({ position: 10 + digit, animate: false })
    const shown = useRef(digit)

    useEffect(() => {
        if (instant) {
            setState({ position: 10 + digit, animate: false })
            shown.current = digit
            return
        }

        if (!active) {
            setState({ position: 0, animate: false })
            shown.current = 0
            return
        }

        let first = 0
        let second = 0

        setState({ position: shown.current, animate: false })

        first = requestAnimationFrame(() => {
            second = requestAnimationFrame(() => {
                setState({ position: 10 + digit, animate: true })
            })
        })

        shown.current = digit

        return () => {
            cancelAnimationFrame(first)
            cancelAnimationFrame(second)
        }
    }, [active, digit, instant])

    return (
        <span className='relative inline-block h-[1.1em] overflow-hidden'>
            <span
                className='flex flex-col'
                style={{
                    transform: `translate3d(0, ${-state.position * 5}%, 0)`,
                    transition: state.animate ? `transform ${duration}ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms` : 'none'
                }}
            >
                {STRIP.map((value, index) => (
                    <span key={index} className='block h-[1.1em] text-center leading-[1.1em]'>
                        {value}
                    </span>
                ))}
            </span>
        </span>
    )
}

interface RollingNumberProps {
    value: string
    active?: boolean
    className?: string
    duration?: number
    stagger?: number
}

/**
 * Odometer-style number. Digits slot-roll into place whenever `active`
 * turns true and again whenever the value changes. Static for visitors
 * who prefer reduced motion.
 */
export const RollingNumber: FC<RollingNumberProps> = ({
    value,
    active = true,
    className,
    duration = 1600,
    stagger = 70
}): ReactNode => {
    const reduced = useReducedMotion()
    const characters = value.split('')
    let digitOrder = 0

    return (
        <span className={cn('relative inline-flex tabular-nums', className)}>
            <span className='sr-only'>
                {value}
            </span>

            <span aria-hidden='true' className='inline-flex'>
                {characters.map((character, index) => {
                    const fromRight = characters.length - index

                    if (/\d/.test(character)) {
                        const order = digitOrder++

                        return (
                            <DigitStrip
                                key={`digit-${fromRight}`}
                                digit={Number(character)}
                                active={active}
                                instant={reduced}
                                delay={order * stagger}
                                duration={duration}
                            />
                        )
                    }

                    return (
                        <span key={`char-${fromRight}`} className='inline-block h-[1.1em] whitespace-pre leading-[1.1em]'>
                            {character}
                        </span>
                    )
                })}
            </span>
        </span>
    )
}
